import { bboxContains, boundingBox, isPointInRing } from './geometry';
import type { Ring, RingSet } from './types';

/** Moving rings between the two persisted fields, `paths` and `cutouts`. */

/**
 * Move openings that no longer sit inside any outer ring back into `paths`.
 *
 * Needed wherever `paths` is rewritten independently of `cutouts` — deleting a line
 * can remove or renumber an outer ring, and an orphaned opening would otherwise be
 * attributed to the first ring by `groupRings` and subtract from the wrong region.
 *
 * An orphan is NOT discarded. The user drew that ring, and with no ring left to be
 * an opening in it is simply an area again, so it returns to `paths` where it is
 * visible and they can decide what to do with it. Note this makes the layer's area
 * go up, which is the honest outcome: nothing is being excluded any more.
 */
export const rehomeOrphanedCutouts = (
	paths: Ring[],
	cutouts?: Ring[]
): { paths: Ring[]; cutouts: Ring[] } => {
	if (!cutouts || cutouts.length === 0) return { paths, cutouts: [] };

	const boxes = paths.map((outer) => boundingBox(outer));

	const stillEnclosed: Ring[] = [];
	const orphaned: Ring[] = [];

	for (const cutout of cutouts) {
		const probe = cutout[0];

		const enclosed =
			probe !== undefined &&
			paths.some((outer, index) => {
				const box = boxes[index];
				return box !== undefined && bboxContains(box, probe) && isPointInRing(probe, outer);
			});

		if (enclosed) stillEnclosed.push(cutout);
		else orphaned.push(cutout);
	}

	return {
		paths: orphaned.length > 0 ? [...paths, ...orphaned] : paths,
		cutouts: stillEnclosed,
	};
};

/** Flatten ring sets back to the persisted `{ paths, cutouts }` shape. */
export const toPersisted = (sets: RingSet[]): { paths: Ring[]; cutouts: Ring[] } => ({
	paths: sets.map((set) => set.outer),
	cutouts: sets.flatMap((set) => set.holes),
});
